import {
  Checkbox,
  CIActionBtnGroup,
  CIActionGuard,
  CIStatusCapsule,
  CITableGrid,
  getLocaleDateTimeFormat,
  Loader,
  Modal,
  NoDataAvailable,
  toLocalDate,
  UnableToFetch,
  useToastMessage,
  useUserPermission,
  usePaginationPreference
} from 'ccs-ui-components';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAllServiceRequest } from '../../../../store/actions/sagaActions/services/GetAllServiceAction';
import { AppDispatch, useSelector } from '../../../../store/AppStore';

import DropdownMenuButton from '../../../../../common/components/UI/menu/DropdownMenuButton';
import { requiredAuthPermission } from '../../../../../common/constants/UserPermissionConstants';
import { getFromDateOrToDate } from '../../../../../common/utils/CommonUtils';
import { REGISTRY_TYPE } from '../../../../../dashboard/constants/CommonConstants';
import { SERVICE_MODE, ServiceLabels, SORT_TYPE, STATUS_TYPES, URL_PATH } from '../../../../constants';
import { ButtonProp, IRecordModel, IService } from '../../../../models';
import useSelectAllCheckBox, { MAX_SELECTION_SIZE } from '../../../../store/actions/actionType/services/UseSelectAllCheckBoxes';
import {
  addClearData,
  addFilterServiceData,
  addSortServiceData,
} from '../../../../store/reducer/services/ServiceFilterReducer';
import { getDateValue, getSortValue, getTypeValue, getValue } from '../../../../utilities/events/EventsListUtils';
import {
  getQueryParamCondition,
  getSortState, getStatusParam,
  isServiceNameEqual,
  paginationParamUtil,
  getFilterParams,
  defaultServiceListStatus
} from '../../../../utilities/services/ServiceListUtils';
import classes from './ServiceList.module.scss';

interface IServiceList {
  selectedExportItem: (items: IRecordModel[]) => void;
  exportLoading: boolean;
  exportSuccess: boolean;
  onDependencyGraphFound: (isDependencyGraphFound: boolean, registryName: string, entityVersion: string, entityStatus: string) => void
}

export const ServiceList = ({ selectedExportItem, exportLoading, exportSuccess, onDependencyGraphFound }: IServiceList) => {
  /* Hooks */
  const { failure, renderToast } = useToastMessage();
  const dispatch = useDispatch<AppDispatch>();
  const { t: translate } = useTranslation();
  const history = useNavigate();
  const route = useLocation();
  const { permissionObject } = useUserPermission();
  const { getPreferredPageSize, persistPageSize } = usePaginationPreference();
  /* Local Variables */

  /* Get data from redux store */
  const { serviceErrorMessage } = useSelector((state) => state.fetchAllServiceReducer);
  const { isLoading } = useSelector((state) => state.fetchAllServiceReducer);
  const { serviceList: listServices } = useSelector((state) => state.fetchAllServiceReducer);
  const { searchText, status, fromDate, toDate, sort, clearData } = useSelector(
    (state) => state.filterServiceReducer
  );

  const params = useMemo(
    () =>
      getFilterParams(route.search, { searchText, status, fromDate, toDate, sort, clearData }),
    [route.search, searchText, status, fromDate, toDate, sort, clearData]
  );

  /* Local states */
  const [page, setPage] = useState<number>(listServices.currentPageNumber);
  const [item, setItem] = useState<number>(params.item ? Number(params.item) : getPreferredPageSize());
  const [sortState, setSortState] = useState<string>(sort !== '' ? sort : SORT_TYPE.UPDATED_TIMESTAMP_DESC);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [queryParams, setQueryParams] = useState<{ [key: string]: string }>(params);
  const [urlServiceName, setUrlServiceName] = useState('');
  const [urlServiceVersion, setUrlServiceVersion] = useState('');
  const [selectedSortField, setSelectedSortField] = useState(queryParams.sort ?? SORT_TYPE.UPDATED_TIMESTAMP);
  const [, setSelectedSortFieldOrder] = useState(!!((queryParams.type && queryParams.type === SORT_TYPE.ASCENDING)));
  const [isCheckAllServiceList, setIsCheckAllServiceList] = useState(false);
  /* States to manage the export configuration of service */
  const [selectedServiceList, setSelectedServiceList] = useState<IRecordModel[]>([]);

  const { handleSelectAllCheckbox, showMaxLimSelectionToast } = useSelectAllCheckBox(selectedServiceList, setIsCheckAllServiceList, failure, setSelectedServiceList, item);

  const statusParam = status && status?.split(',').length >= 5 ? '' : status;

  const queryParam = {
    searchText,
    status: status !== undefined ? statusParam : '',
    fromDate,
    toDate
  };

  const onGetPageHandler = (pageNumber: number) => {
    setPage(pageNumber);
    setIsCheckAllServiceList(false);
    setQueryParams((pre) => ({
      ...pre,
      page: String(pageNumber)
    }));
  };

  const onGetItemsHandler = (itemNumber: number) => {
    setItem(itemNumber);
    setQueryParams((pre) => ({
      ...pre,
      item: String(itemNumber)
    }));
  };

  useEffect(() => {
    if (page >= 0 || clearData !== ButtonProp.refresh) {
      setFilterStates();
    }
  }, [item, page]);

  /**
   * Navigates to view service screen
   * @param serviceName service name
   * @param receivedStatus service status
   */
  const navigateToServicePreview = (serviceName: string, serviceVersion: string) => {
    history(
      `${URL_PATH.SERVICE_HOME}${URL_PATH.VIEW_SERVICE}/${serviceName}/${serviceVersion}/${SERVICE_MODE.VIEW}`,
      { state: { path: route.search } }
    );
  };

  /**
   * Navigates to edit service screen
   * @param serviceName service name
   * @param status service status
   */
  const navigateToServiceEdit = (serviceName: string, serviceVersion: string, receivedStatus: string) => {
    if (receivedStatus?.toLocaleUpperCase() === STATUS_TYPES.ACTIVE || receivedStatus?.toLocaleUpperCase() === STATUS_TYPES.INACTIVE) {
      setShowConfirmDialog(true);
      setUrlServiceName(serviceName);
      setUrlServiceVersion(serviceVersion);
    } else {
      history(
        `${URL_PATH.SERVICE_HOME}${URL_PATH.VIEW_SERVICE}/${serviceName}/${serviceVersion}/${SERVICE_MODE.EDIT}`,
        { state: { path: route.search } }
      );
    }
  };

  const handleCloseConfirmDialog = () => setShowConfirmDialog(false);

  const handleConfirmDialog = () => {
    setShowConfirmDialog(false);
    setUrlServiceName('');
    setUrlServiceVersion('');
    history(
      `${URL_PATH.SERVICE_HOME}${URL_PATH.VIEW_SERVICE}/${urlServiceName}/${urlServiceVersion}/${SERVICE_MODE.EDIT}`,
      { state: { path: route.search } }
    );
  };

  useEffect(() => {
    if (clearData === ButtonProp.clear) {
      // Loop when user Clear the filter's
      setPage(0);
      setQueryParams((pre) => ({
        ...pre,
        status: defaultServiceListStatus,
        fromDate: '',
        toDate: '',
        page: String(0)
      }));
    } else if (clearData === ButtonProp.apply) {
      // Loop when user Apply the filter's
      setPage(0);
      setSortState(SORT_TYPE.UPDATED_TIMESTAMP_DESC);
      setQueryParams((pre) => ({
        ...pre,
        sort: getSortValue(queryParams),
        type: getTypeValue(queryParams),
        status: getValue(status ?? ''),
        serviceName: getValue(searchText ?? ''),
        fromDate: getDateValue(fromDate),
        toDate: getDateValue(toDate),
        page: String(0)
      }));
    } else if (clearData === ButtonProp.search) {
      // Loop when user search the result from search event's
      setPage(0);
      setSortState(SORT_TYPE.UPDATED_TIMESTAMP_DESC);
      setQueryParams((pre) => ({
        ...pre,
        sort: getSortValue(queryParams),
        type: getTypeValue(queryParams),
        serviceName: getValue(searchText ?? ''),
        page: String(0) ?? ''
      }));
    } else if (clearData === ButtonProp.refresh && route.search === '') {
      // Loop when user search refresh the page
      setQueryParams((pre) => ({
        ...pre,
        sort: getSortValue(queryParams),
        type: getTypeValue(queryParams),
        status: getValue(status ?? ''),
        serviceName: getValue(searchText ?? ''),
        fromDate: getDateValue(fromDate),
        toDate: getDateValue(toDate)
      }));
      dispatch(getAllServiceRequest(page, item, queryParam, sortState));
    }
  }, [clearData]);

  const setFilterStates = () => {
    dispatch(addClearData(ButtonProp.refresh));
    const sortState = getSortState(queryParams);
    setSortState(
      isServiceNameEqual(queryParams)
        ? `${queryParams.sort},${queryParams.type}`
        : sortState
    );
    dispatch(
      addSortServiceData(
        queryParams.sort !== ''
          ? `${queryParams.sort},${queryParams.type}`
          : SORT_TYPE.UPDATED_TIMESTAMP_DESC
      )
    );
    const queryParamCondition = getQueryParamCondition(queryParams);
    const statusParamQuery = status && status.split(',').length >= 5 ? '' : queryParamCondition;
    const statusParam = getStatusParam(queryParams, status)
      ? statusParamQuery
      : queryParams.status;
    paginationParamUtil(setPage, setItem, queryParams, getPreferredPageSize(),);
    if (route.search !== '') {
      const queryParam = {
        searchText: queryParams.serviceName ?? '',
        status: statusParam,
        fromDate: getFromDateOrToDate(queryParams.fromDate),
        toDate: getFromDateOrToDate(queryParams.toDate)
      };
      dispatch(addFilterServiceData(queryParam));
      dispatch(
        getAllServiceRequest(
          page,
          item,
          queryParam,
          queryParams.sort === '' || queryParams.sort === undefined
            ? sortState
            : `${queryParams.sort},${queryParams.type}`
        )
      );
    } else {
      dispatch(getAllServiceRequest(page, item, queryParam, sortState));
    }
  };

  useEffect(() => {
    if (Object.keys(queryParams).length === 0) {
      setSortState(SORT_TYPE.UPDATED_TIMESTAMP_DESC);
    } else {
      // function is for remove empty value from the URL
      const searchParams = new URLSearchParams();
      Object.entries(queryParams).forEach(([k, v]) => {
        if (v) {
          searchParams.set(k, v);
        }
      });
      setFilterStates();
      const queryString = searchParams.toString();
      history(
        queryString ? `?${queryString}` : route.search,
        { state: route.search }
      );
    }
  }, [queryParams]);

  const sortHandler = (label: string, selectedLabelState: boolean) => {
    setQueryParams((pre: { [key: string]: string }) => ({
      ...pre,
      sort: label,
      type: selectedLabelState ? SORT_TYPE.ASCENDING : SORT_TYPE.DESCENDING
    }));
  };

  const handleClick = (e: React.MouseEvent<HTMLElement>, label: string) => {
    if (selectedSortField === label) {
      setSelectedSortFieldOrder((preValue) => {
        sortHandler(selectedSortField, !preValue);
        return !preValue;
      });
    } else {
      setSelectedSortField(label);
      setSelectedSortFieldOrder(true);
      sortHandler(label, true);
    }
  };

  useEffect(() => {
    selectedExportItem(selectedServiceList);
    if (exportSuccess) {
      setSelectedServiceList([]);
      selectedExportItem([]);
    }
  }, [selectedServiceList, exportSuccess]);

  useEffect(() => {
    if (queryParams.item) {
      persistPageSize(Number(queryParams.item));
    }
  }, [queryParams.item]);

  const footerOptionsObj = {
    itemsPerPage: item,
    currentPageNumber: page,
    numberOfPages: listServices.numberOfPages,
    onCurrentPageNumberChange: onGetPageHandler,
    onItemsPerPageChange: onGetItemsHandler
  };

  const footerObj = { footerType: 'paging', footerOptions: footerOptionsObj };

  /**
   * When individual active journeys checkbox is selected
   */
  const handleSelectCheckbox = (name: React.ChangeEvent<HTMLInputElement>, version: number) => {
    const { id, checked } = name.target;
    if (checked && selectedServiceList.length >= MAX_SELECTION_SIZE) {
      showMaxLimSelectionToast();
    } else {
      setSelectedServiceList([...selectedServiceList, { recordName: id, recordVersion: version }]);
    }
    if (!checked) {
      setSelectedServiceList(selectedServiceList.filter((item) => JSON.stringify(item) !== JSON.stringify({ recordName: id, recordVersion: version })));
      setIsCheckAllServiceList(false);
    }
  };

  const renderServiceStatus = (row: IService) => (
    <div>
      {
        row?.status?.toLocaleUpperCase() === STATUS_TYPES.RETURNED ? (
          <CIStatusCapsule status={row.status} comment={row.serviceStatusHistory?.find((item) => item.status.toLocaleUpperCase() === STATUS_TYPES.RETURNED)?.comment} />
        ) : (
          <CIStatusCapsule status={row.status ?? ''} />
        )
      }
    </div>
  );

  const renderLastModified = (row: IService) => {
    const date = toLocalDate({ date: row?.updatedTimestamp ?? '' });
    return (
      <div className="service-list-lastmodified" title={date}>{date}</div>
    );
  };

  const onDependencyGraphFoundForService = (isDependencyGraphFound: boolean, registryName: string, entityVersion: string, entityStatus: string) => {
    onDependencyGraphFound(isDependencyGraphFound, registryName, entityVersion, entityStatus);
  };

  /**
  * Gets the updated events
  */
  const onDeleteSuccess = () => {
    onGetPageHandler(0);
  };

  const navigateToServiceDependency = (serviceName: string, version: string, status: string) => {
    history(
      `${URL_PATH.SERVICE_HOME}${URL_PATH.SERVICE_DEPENDENCY}`,
      { state: { associationServiceName: serviceName, associationVersion: version, associationStatus: status } }
    );
  };

  const renderActionBtn = (row: IService) => (
    <div className={classes['action-col']}>
      <CIActionBtnGroup
        buttons={[{
          label: `${translate('info_message.msg_view_association')}`,
          key: 'dependencyIcon',
          onClick: async () => navigateToServiceDependency(row.serviceName, `${row.version}`, `${row.status}`),
          show: CIActionGuard([requiredAuthPermission.Services.view], permissionObject.permission),
          testId: 'view-btn',
          disabled: row.status?.toLocaleUpperCase() !== STATUS_TYPES.ACTIVE
        },
        {
          label: 'view',
          key: 'viewIcon',
          onClick: async () => navigateToServicePreview(row.serviceName, `${row.version}`),
          show: CIActionGuard([requiredAuthPermission.Services.view], permissionObject.permission),
          testId: 'view-btn'
        },
        {
          label: 'edit',
          key: 'editIcon',
          disabled: row.status?.toLocaleUpperCase() === STATUS_TYPES.PENDING_APPROVAL || row.status?.toLocaleUpperCase() === STATUS_TYPES.INACTIVE,
          onClick: async () => navigateToServiceEdit(row.serviceName, `${row.version}`, `${row.status}`),
          show: CIActionGuard([requiredAuthPermission.Services.edit, requiredAuthPermission.Services.approve], permissionObject.permission),
          testId: 'edit-btn'
        }]}
      />
      <DropdownMenuButton
        status={`${row?.status}`}
        deleteURL={`services/${row.serviceName}/version/${row.version}`}
        onDeleteSuccess={onDeleteSuccess}
        showOn="list"
        registryType={REGISTRY_TYPE.SERVICE}
        registryName={row.serviceName}
        entityData={row}
        onDependencyGraphFound={onDependencyGraphFoundForService}
      />
    </div>
  );

  const updatedBy = (row: IService) => (
    <div className="last-modified-data" title={row.updatedBy}>{row.updatedBy}</div>
  );

  const renderServiceBody = (row: IService) => (
    <div className={classes['render-service-body']}>
      <Checkbox
        type="checkbox"
        onChange={(e) => handleSelectCheckbox(e, parseInt(row.version || '0', 10))}
        id={row.serviceName}
        checked={
          selectedServiceList.some((item) => item.recordName === row.serviceName && item.recordVersion === parseInt(row.version || '0', 10))
          && row.status?.toLocaleUpperCase() === STATUS_TYPES.ACTIVE
        }
        disabled={row.status?.toLocaleUpperCase() !== STATUS_TYPES.ACTIVE}
        data-testid="selectCheckBox"
      />
      <div className={classes['body-render']}>
        <div title={row.serviceName} className={classes['body-render-subclass']}>
          <p className="service-list-servicename">
            {row.serviceName}
          </p>
        </div>
        <p className="service-list-version">
          {translate('service_list_component.txt_version', {
            replace: {
              version: row.version
            }
          })}
        </p>
      </div>
    </div>
  );

  return (
    <div className={classes['service-list']}>
      {renderToast()}
      {(serviceErrorMessage !== '' && !isLoading) && (
        <UnableToFetch />
      )}
      {(serviceErrorMessage === '' && !isLoading && !listServices.services?.length) && (
        <NoDataAvailable />
      )}
      <Loader show={isLoading || exportLoading} />
      {!serviceErrorMessage && listServices?.services && listServices?.services?.length !== 0 && (
        <div className={classes['service-list-table-data']}>
          {listServices?.services[0]?.serviceName !== '' ? (
            <div>
              <CITableGrid
                headers={[
                  {
                    name: ServiceLabels.SERVICE_NAME,
                    label: (
                      <div className={classes['render-service-header']}>
                        <Checkbox
                          type="checkbox"
                          id="selectAll"
                          onChange={handleSelectAllCheckbox}
                          checked={isCheckAllServiceList}
                          disabled={listServices.services.length > MAX_SELECTION_SIZE || listServices.services.some((item) => item?.status?.toLocaleUpperCase() !== STATUS_TYPES.ACTIVE)}
                          data-testid="selectAllCheckBox"
                        />
                        <div className={classes['header-render']}>{translate('service_list_component.lbl_service_name')}</div>
                      </div>),
                    sortOptions: {
                      callback: handleClick
                    },
                    headerClassName: 'check-box-input',
                    options: { breakPointWidth: 4 },
                    columnData: {
                      field: ServiceLabels.SERVICE_NAME, render: renderServiceBody
                    }
                  },
                  {
                    name: ServiceLabels.SERVICE_STATUS,
                    label: translate('service_list_component.lbl_status'),
                    sortOptions: {
                      callback: handleClick
                    },
                    options: { breakPointWidth: 2 },
                    columnData: {
                      field: ServiceLabels.SERVICE_STATUS, render: renderServiceStatus
                    }
                  },
                  {
                    name: ServiceLabels.LAST_MODIFIED,
                    label: translate('service_list_component.lbl_last_modified'),
                    sortOptions: {
                      callback: handleClick
                    },
                    options: { breakPointWidth: 2 },
                    labelOptions: { helperText: `(${getLocaleDateTimeFormat()})`, helperTextClass: 'updatedon-sublabel' },
                    columnData: {
                      field: '', render: renderLastModified
                    }
                  },
                  {
                    name: ServiceLabels.UPDATED_BY,
                    label: translate('service_list_component.lbl_modified_by'),
                    sortOptions: {
                      callback: handleClick
                    },
                    options: { breakPointWidth: 2 },
                    columnData: {
                      field: ServiceLabels.UPDATED_BY,
                      render: updatedBy
                    }
                  },
                  {
                    name: 'action',
                    label: '',
                    options: { breakPointWidth: 2 },
                    columnData: {
                      field: ServiceLabels.ACTION_BTN, render: renderActionBtn, className: 'button-alignment'
                    }
                  },
                ]}
                sorted={{ sortedName: selectedSortField, sortedOrder: queryParams.type }}
                footer={footerObj}
                tableData={listServices.services}
              />

            </div>
          ) : (
            null
          )}
        </div>
      )}
      <Modal
        modalTitle={translate('service_wrapper.lbl_new_version')}
        btnCloseText={String(translate('service_wrapper.lbl_cancel'))}
        btnSaveText={String(translate('service_wrapper.lbl_proceed'))}
        modalBody={translate('service_list_wrapper_component.lbl_continue_edit_service')}
        modalShow={showConfirmDialog}
        onCloseModal={handleCloseConfirmDialog}
        onSaveModal={handleConfirmDialog}
        alignToCentre
        disableSaveBtn={false}
        disableCloseBtn={false}
        type="warning"
      />
    </div>
  );
};
